'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { useRef, useState } from 'react';

import { deleteCustomer } from '@eliadmin/core/src/api/customers/customers-api';
import { PROFILE_COPY } from '@eliadmin/core/src/components/dlz/account/profile/profile-copy';
import { DialogCard } from '@eliadmin/core/src/components/dlz/dialogs/dialog-card';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

type DeleteStatus = 'idle' | 'deleting' | 'failed';

/**
 * Figma „Delete account“ (60321:19325): DLZ/Body/S Medium in text/tertiary. It asks first – the safe
 * „Ponechať konto“ takes the focus – and after `deleteCustomer` a full navigation to the sign-out
 * route drops the session cookie.
 */
export function DeleteAccount({ authToken }: { authToken: string }) {
  const locale = useLocaleStore(s => s.currentLocale);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<DeleteStatus>('idle');
  const keepRef = useRef<HTMLButtonElement>(null);
  const copy = PROFILE_COPY.deleteAccount;

  const remove = async () => {
    setStatus('deleting');
    try {
      await deleteCustomer({ locale, authToken });
      window.location.assign(ROUTES.SIGN_OUT());
    } catch {
      setStatus('failed');
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={next => {
        if (status === 'deleting') return;
        setOpen(next);
        setStatus('idle');
      }}
    >
      <Dialog.Trigger className="dlz-body-s-medium shrink-0 rounded-sm text-[#6B7079] transition-colors hover:text-[#0F1012] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]">
        {copy.action}
      </Dialog.Trigger>
      <DialogCard
        title={copy.title}
        description={copy.text}
        className="max-w-[539px]"
        onOpenAutoFocus={event => {
          event.preventDefault();
          keepRef.current?.focus();
        }}
      >
        {status === 'failed' ? (
          <p role="alert" className="dlz-body-s text-[#D7191F]">
            {copy.failed}
          </p>
        ) : null}
        <div className="flex flex-col gap-3 md:flex-row">
          <Button
            type="button"
            onClick={remove}
            isLoading={status === 'deleting'}
            className="h-12 md:h-[52px]"
          >
            {copy.action}
          </Button>
          <Dialog.Close asChild>
            <Button
              ref={keepRef}
              type="button"
              variant="secondary"
              disabled={status === 'deleting'}
              className="h-12 md:h-[52px]"
            >
              {copy.keep}
            </Button>
          </Dialog.Close>
        </div>
      </DialogCard>
    </Dialog.Root>
  );
}
